
import { Component, ViewChild, input, output } from '@angular/core';
import { FormGroup, ReactiveFormsModule } from '@angular/forms';
import { MatBadgeModule } from '@angular/material/badge';
import { MatButtonModule } from '@angular/material/button';
import { MatChipsModule } from '@angular/material/chips';
import {
    MatExpansionModule,
    MatExpansionPanel,
} from '@angular/material/expansion';
import { MatIcon } from '@angular/material/icon';
import { MatTooltip } from '@angular/material/tooltip';
import { TranslatePipe } from '@ngx-translate/core';
import { ActiveFilterChip } from '../../../models';
import {
    CheckboxFieldComponent,
    DatepickerFieldComponent,
    InputFieldComponent,
    MultiSelectFieldComponent,
    RadioFieldComponent,
    SelectFieldComponent,
    SelectTipologicaFieldComponent,
    SwitchFieldComponent,
    TextareaFieldComponent,
} from '../../form';
import { FilterFieldConfig } from '../models/search-filters.model';

@Component({
   
  imports: [
    MatExpansionModule,
    MatIcon,
    MatBadgeModule,
    ReactiveFormsModule,
    InputFieldComponent,
    MatButtonModule,
    SelectFieldComponent,
    SelectTipologicaFieldComponent,
    CheckboxFieldComponent,
    DatepickerFieldComponent,
    MultiSelectFieldComponent,
    TextareaFieldComponent,
    RadioFieldComponent,
    SwitchFieldComponent,
    TranslatePipe,
    MatTooltip,
    MatChipsModule
],
  selector: 'siat-search-filters',
  templateUrl: './search-filters.component.html',
})
export class SearchFiltersComponent {
  /**
   * Filter configuration received from the parent component.
   */
  filterConfig = input<FilterFieldConfig[]>([]);

  /**
   * Form group for the filter inputs.
   */
  filterForm = input<FormGroup>();

  /**
   * On click event for the "Apply Filters" button.
   */
  filtersApplied = output<void>();

  /**
   * On click event for the "Reset Filters" button.
   */
  filtersReset = output<void>();

  /**
   * Riferimento al pannello
   */
  @ViewChild(MatExpansionPanel) panel: MatExpansionPanel;

  /**
   * Stores the filter values only after the user clicks "Apply".
   */
  private appliedFiltersState: Record<string, unknown> = {};

  /**
   * Apply the filters and emit an event.
   */
  applyFilters(): void {
    if (!this.filterForm().valid) return;

    const rawValues = this.filterForm().value;

    for (const key in rawValues) {
      const value = rawValues[key];
      if (typeof value === 'string' && value.trim() === '') {
        this.filterForm().get(key)?.setErrors({ whitespace: true });
        this.removeFilter(key);
      }
    }

    const filtered: Record<string, unknown> = {};

    for (const key in rawValues) {
      const value = rawValues[key];
      if (value !== null && value !== '' && value !== undefined) {
        filtered[key] = value;
      }
    }
    Object.keys(this.filterForm().controls).forEach(key => {
      const control = this.filterForm().get(key);
      if (control && typeof control.value === 'string') {
        control.setValue(control.value.trim(), { emitEvent: false });
      }
    });

    this.appliedFiltersState = this.filterForm().value;
    this.filtersApplied.emit();
  }

  /**
   * Reset all filters and emit an event.
   */
  resetFilters(): void {
    this.appliedFiltersState = {};
    this.filtersReset.emit();
    if (this.panel) this.panel.close();
  }

  /**
   * Remove a specific filter by its key.
   * This method resets the form control for the specified filter key
   */
  removeFilter(key: string): void {
    const control = this.filterForm().get(key);
    if (control) {
      control.reset();
    }

    this.appliedFiltersState = this.filterForm().value;
    this.filtersApplied.emit();
  }

  /**
   *  Get the active filters as an array of ActiveFilterChip objects.
   *  This method iterates through the filter form values and constructs
   *  an array of active filters that are not primary filters.
   */
  get activeFilters(): ActiveFilterChip[] {
    if (!this.filterForm) return [];

    const formValue = this.appliedFiltersState;

    return this.filterConfig()
      .map(field => {
        const value = formValue[field.controlName];
        if (!value) return null;

        let displayValue: string;
        if (
          typeof value === 'object' &&
          (value as { name?: string })?.name
        ) {
          displayValue = (value as { name: string }).name;
        } else if (typeof value === 'boolean') {
          displayValue = value ? 'Sì' : 'No';
        } else {
          displayValue = String(value);
        }

        return {
          key: field.controlName,
          label: field.label,
          value: displayValue,
        } as ActiveFilterChip;
      })
      .filter((chip): chip is ActiveFilterChip => chip !== null);
  }

  /**
   * Get the count of active filters.
   * This is used to display the number of active filters in the badge.
   * @returns The count of active filters.
   */
  get activeFilterCount(): number {
    return this.activeFilters.length;
  }
}
